import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Printer, FileDown, FileSpreadsheet, FileText, Lock } from "lucide-react";
import { formatUSD, formatDate, monthName } from "@/lib/format";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import CoreLogo from "@/components/CoreLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicSharePage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [busy, setBusy] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/public/share/${token}`);
      setState({ loading: false, error: null, data: res.data });
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setState({ loading: false, error: typeof detail === "string" ? detail : "Share link unavailable", data: null });
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDownload = async (kind) => {
    setBusy(kind);
    try {
      const res = await axios.get(`${API}/public/share/${token}/${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tax-report.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch (err) {
      // Surface the error to the user; no need to also log to console (lint: no-console).
      toast.error("Download failed");
      return;
    } finally {
      setBusy(null);
    }
  };

  if (state.loading) return <div className="min-h-screen flex items-center justify-center text-sm text-neutral-500">Loading shared report…</div>;

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="max-w-md text-center border border-neutral-200 p-10">
          <Lock className="h-8 w-8 mx-auto text-neutral-400 mb-4" strokeWidth={1.5} />
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">Share Link</div>
          <h1 className="font-serif text-3xl mb-3">Unavailable</h1>
          <p className="text-sm text-neutral-600">{state.error}</p>
          <p className="text-xs text-neutral-400 mt-6">Ask the agent for a new link.</p>
        </div>
      </div>
    );
  }

  const { agent, label, report, theme: publicTheme } = state.data;
  const brokerageDisplay = publicTheme?.theme?.display_name || agent.brokerage || "—";
  const customLogo = publicTheme?.theme?.logo_data_url;
  const footerText = publicTheme?.theme?.footer_text;
  const t = report.totals;

  return (
    <div className="min-h-screen bg-white text-neutral-900 print:p-0" data-testid="public-share-page">
      {/* Top bar (hidden in print) */}
      <header className="border-b border-neutral-200 print:hidden">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-neutral-900">
              <CoreLogo variant="mark" size={36} />
            </div>
            <div className="flex flex-col">
              <span className="font-serif text-xl tracking-tight leading-none">Ledger</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-400 mt-1">Shared Tax Report</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => window.print()} variant="outline" className="rounded-none border-neutral-300 hover:bg-neutral-100 h-9" data-testid="public-print-button">
              <Printer className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Print
            </Button>
            <Button onClick={() => handleDownload("pdf")} disabled={busy === "pdf"} className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white h-9" data-testid="public-pdf-button">
              <FileText className="h-4 w-4 mr-2" strokeWidth={1.5} />
              PDF
            </Button>
            <Button onClick={() => handleDownload("excel")} disabled={busy === "excel"} className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white h-9" data-testid="public-excel-button">
              <FileSpreadsheet className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Excel
            </Button>
            <Button onClick={() => handleDownload("csv")} disabled={busy === "csv"} variant="outline" className="rounded-none border-neutral-300 hover:bg-neutral-100 h-9" data-testid="public-csv-button">
              <FileDown className="h-4 w-4 mr-2" strokeWidth={1.5} />
              CSV
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 print:p-0">
        {/* Document header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between border-b border-neutral-900 pb-4 mb-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">Schedule of Income &amp; Expenses</div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">End-of-Year Tax Report</h2>
            <div className="font-mono text-sm text-neutral-700 mt-2">{label}</div>
          </div>
          <div className="flex items-end gap-6 mt-4 sm:mt-0">
            <div className="text-right">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">Prepared for CPA by</div>
              <div className="font-serif text-xl text-neutral-900 mt-1">{agent.full_name}</div>
              <div className="font-mono text-xs text-neutral-500">
                {brokerageDisplay} {agent.license_no ? `· Lic # ${agent.license_no}` : ""}
              </div>
            </div>
            <div className="hidden sm:block text-neutral-900 border-l border-neutral-200 pl-6" data-testid="public-share-core-logo">
              {customLogo ? (
                <img src={customLogo} alt={brokerageDisplay} style={{ height: 52, width: "auto", maxWidth: 90 }} />
              ) : (
                <CoreLogo variant="mark" size={52} />
              )}
            </div>
          </div>
        </div>

        {/* Summary block */}
        <section className="mb-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">I. Profit &amp; Loss Summary</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-neutral-300">
            {[
              ["Closed Transactions", t.transaction_count, null],
              ["Broker Fees / Splits", formatUSD(t.broker_fees), null],
              ["Total Sales Volume", formatUSD(t.total_sales), null],
              ["Referral Fees", formatUSD(t.referral_fees), null],
              ["Gross Commission", formatUSD(t.gross_commission), null],
              ["Transaction Fees", formatUSD(t.transaction_fees), null],
              ["Net Commission", formatUSD(t.net_commission), "emphasis"],
              ["Total Expenses", formatUSD(t.total_expenses), "expense"],
            ].map(([label, value, mod]) => (
              <div key={label} className="flex items-center justify-between px-5 py-3 border-b border-neutral-200 even:bg-neutral-50/50">
                <span className={`text-sm ${mod === "emphasis" ? "font-semibold text-neutral-900" : "text-neutral-700"}`}>{label}</span>
                <span
                  className={`font-mono ${mod === "emphasis" ? "text-lg font-semibold" : "text-base"}`}
                  style={mod === "expense" ? { color: "var(--expense)" } : {}}
                >
                  {value}
                </span>
              </div>
            ))}
          </div>
          <div className="border border-neutral-900 border-t-0 flex items-baseline justify-between px-5 py-4 bg-neutral-50">
            <div className="font-serif text-xl">Net Profit</div>
            <div className="font-mono text-3xl" style={{ color: t.net_profit >= 0 ? "var(--profit)" : "var(--expense)" }}>
              {formatUSD(t.net_profit)}
            </div>
          </div>
        </section>

        {/* Closed transactions */}
        <section className="mb-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">II. Closed Transactions</div>
          <div className="overflow-x-auto border border-neutral-300">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-900 text-white">
                  {["Date", "Property", "Client", "Side", "Sale", "%", "Gross", "Broker Fees", "Referral", "Txn", "Net"].map((h) => (
                    <th key={h} className={`font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2 ${h === "Date" || h === "Property" || h === "Client" || h === "Side" ? "text-left" : "text-right"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.deals.length === 0 ? (
                  <tr><td colSpan={11} className="text-center text-neutral-500 py-8 text-sm">No closed transactions in this period.</td></tr>
                ) : report.deals.map((d) => (
                  <tr key={d.id} className="row-hover border-t border-neutral-200">
                    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{formatDate(d.close_date)}</td>
                    <td className="px-3 py-2">{d.property_name}</td>
                    <td className="px-3 py-2 text-neutral-600">{d.client_name || "—"}</td>
                    <td className="px-3 py-2 text-neutral-600 capitalize">{d.side}</td>
                    <td className="px-3 py-2 font-mono text-right">{formatUSD(d.sale_price)}</td>
                    <td className="px-3 py-2 font-mono text-right text-neutral-600">{Number(d.commission_rate).toFixed(2)}%</td>
                    <td className="px-3 py-2 font-mono text-right">{formatUSD(d.gross_commission)}</td>
                    <td className="px-3 py-2 font-mono text-right" style={{ color: "var(--expense)" }}>{formatUSD(d.broker_fees)}</td>
                    <td className="px-3 py-2 font-mono text-right" style={{ color: "var(--expense)" }}>{formatUSD(d.referral_fee)}</td>
                    <td className="px-3 py-2 font-mono text-right" style={{ color: "var(--expense)" }}>{formatUSD(d.transaction_fee)}</td>
                    <td className="px-3 py-2 font-mono text-right font-semibold">{formatUSD(d.net_commission)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Expenses by category */}
        <section className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">III. Deductible Expenses — By Category</div>
            <div className="border border-neutral-300">
              <table className="w-full text-sm">
                <thead className="bg-neutral-900 text-white">
                  <tr>
                    <th className="text-left font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">Category</th>
                    <th className="text-right font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {report.expenses_by_category.length === 0 ? (
                    <tr><td colSpan={2} className="text-center text-neutral-500 py-6">No expenses logged.</td></tr>
                  ) : report.expenses_by_category.map((row) => (
                    <tr key={row.category} className="border-t border-neutral-200">
                      <td className="px-3 py-2">{row.category}</td>
                      <td className="px-3 py-2 font-mono text-right" style={{ color: "var(--expense)" }}>{formatUSD(row.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="print:hidden">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">Monthly Trend</div>
            <div className="border border-neutral-300 p-4 h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={report.monthly.map((m) => ({ ...m, label: monthName(m.month) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#525252", fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#E5E5E5" }} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#525252", fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#0A0A0A", color: "#FFF", border: "none", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 11 }} formatter={(v) => formatUSD(v)} cursor={{ fill: "#FAFAFA" }} />
                  <Bar dataKey="net" fill="#0A0A0A" name="Net Comm." />
                  <Bar dataKey="expenses" fill="#7F1D1D" name="Expenses" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        <div className="mt-10 pt-4 border-t border-neutral-200 text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-400 leading-relaxed" data-testid="public-share-footer-text">
          {footerText || "Read-only CPA share · Generated by Ledger · Net commission and net profit are computed from agent-entered records."}
        </div>
      </div>
    </div>
  );
}
